'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useToggleFavorite } from '@/features/design/history';

interface UseConceptFavoriteOptions {
  projectId: string;
  conceptId: string;
  isFavorite: boolean;
  /** Optional: callback after a successful toggle reaches the server. */
  onToggled?: (newIsFavorite: boolean) => void;
}

/**
 * Single source of the concept favorite toggle: optimistic local state +
 * debounced mutation. Shared by `FavoriteButton` (the standalone header
 * button) and the concept-page kebab favorite item so the debounced
 * mutation logic isn't duplicated across the two entry points.
 */
export function useConceptFavorite({
  projectId,
  conceptId,
  isFavorite,
  onToggled,
}: UseConceptFavoriteOptions) {
  // Optimistic local state for instant visual feedback
  const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite);

  // Sync with prop when it changes (e.g., after server response or parent update)
  useEffect(() => {
    setOptimisticFavorite(isFavorite);
  }, [isFavorite]);

  const toggleFavoriteMutation = useToggleFavorite();

  // Debounce timer — coalesce rapid taps into a single API call
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const serverValueRef = useRef(isFavorite);

  // Track server-known value
  useEffect(() => {
    serverValueRef.current = isFavorite;
  }, [isFavorite]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const toggle = useCallback(() => {
    if (!projectId || !conceptId) {
      console.warn('useConceptFavorite: Missing projectId or conceptId', {
        projectId,
        conceptId,
      });
      return;
    }

    const newValue = !optimisticFavorite;
    setOptimisticFavorite(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // If toggled back to server state, skip API call
      if (newValue === serverValueRef.current) return;

      toggleFavoriteMutation.mutate(
        { projectId, conceptId, newIsFavorite: newValue },
        {
          onSuccess: () => {
            serverValueRef.current = newValue;
            onToggled?.(newValue);
          },
          onError: () => {
            setOptimisticFavorite(serverValueRef.current);
          },
        }
      );
    }, 300);
  }, [
    projectId,
    conceptId,
    optimisticFavorite,
    toggleFavoriteMutation,
    onToggled,
  ]);

  return { isFavorite: optimisticFavorite, toggle };
}
